import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router'
import { Row, Col, Alert, Badge } from 'react-bootstrap'
import * as s from './Preseason.scss'
import PreseasonDivision from './PreseasonDivision'
import PreseasonCountdown from './PreseasonCountdown'
import PreseasonTeamRegistration from './PreseasonTeamRegistration'
import { loadPreseason, divisionStandings, teamRegistrationReset,
    checkTeamNameAvailability, teamRegistrationDivisionSelection, 
    teamRegistrationIsPaid, teamRegistrationRegisterTeam } from '@severed-links/common.redherrings-reducers/football'
import { forEach, uniq, flatten, sum } from 'lodash'
import pluralize from 'pluralize'
import moment from 'moment-timezone'
import FontAwesomeIcon from '@severed-links/common.font-awesome-icon'

const Preseason = () => {

    const dispatch = useDispatch()
    const navigate = useNavigate()
    const params = useParams()
    const seasonName = params.seasonName || null
    const seasons = useSelector(state => state.football.seasons)
    const playerId = useSelector(state => state.account._id)
    const userName = useSelector(state => state.account.userName)
    const season = seasons.find(i => i.seasonName === seasonName)
    const seasonId = (season || {}).seasonId || null
    const preseason = useSelector(state => state.football.preseasons)[`${seasonId}`] || {}
    const leagueStandings = useSelector(state => state.football.leagueStandings)
    const standings = (leagueStandings.find(x => seasonId && x.seasonId === seasonId) || {}).leagueStandings || []
    const teamRegistration = useSelector(state => state.football.teamRegistration)
    const isTeamRegistrationOpen = useSelector(state => state.football.isTeamRegistrationOpen)
    const paymentEmail = useSelector(state => state.football.paymentEmail)
    const _allStandings = flatten((standings || []).map(i => i.standings))
    const hasTeamRegistered = _allStandings.some(i => i.playerId == playerId)
    const registeredTeam = _allStandings.find(i => i.playerId == playerId) || {}
    const registeredDivision = (standings || []).find(i => (i.standings || [])
        .some(team => team.playerId == playerId)) || {}
    const [registrationCheckCompleted, setRegistrationCheckCompleted] = useState(false)
    const [isInitialLoadComplete, setInitialLoadComplete] = useState(false)

    useEffect(() => { loadPreseasonData(seasonId) }, [seasonId])

    const getStandings = () => dispatch(divisionStandings(seasonId))

    const loadPreseasonData = async () => {
        if (seasonId) {
            setRegistrationCheckCompleted(false)
            setInitialLoadComplete(false)
            try {
                await dispatch(loadPreseason(seasonId))
                await getStandings(seasonId)
            } catch (error) {
                setRegistrationCheckCompleted(true)
                setInitialLoadComplete(true)
                throw error
            }
            setRegistrationCheckCompleted(true)
            setInitialLoadComplete(true)
        }
    }

    if (!preseason) return null
    const { nflKickoff, divisions, entryFee, maxRegisteredTeams } = preseason
    var arrStandings = []
    if (standings) {
        forEach(standings, (d, index) => { 
            arrStandings.push(<Col lg={4} md={6} sm={12} xs={12} key={d.divisionCode} className={s.divisionCode}><PreseasonDivision division={d} playerId={playerId} /></Col>)
        })
    }
    const countTotals = (standings || []).map(d => (d.standings || []).length)
    const totalRegistrations = sum(countTotals)
    const hasCapacityLimit = Number.isFinite(maxRegisteredTeams)
    const spotsLeft = hasCapacityLimit ? Math.max(0, maxRegisteredTeams - totalRegistrations) : null
    const counts = uniq(countTotals)
    const multipleLengths = totalRegistrations > 0 && counts.length > 1
    const hasRegisteredTeams = totalRegistrations > 0
    const hasKickoff = Boolean(nflKickoff && moment(nflKickoff).isValid())
    var spotsLeftVariant = 'info'
    if (spotsLeft <= 5) spotsLeftVariant = 'danger'
    else if (spotsLeft <= 10) spotsLeftVariant = 'warning'
    return (
        <main className={s.container}>
            <header className={s.preseasonHeading}>
                <div className={s.headingCopy}>
                    <div className={s.eyebrow}>REDHERRINGS Football Pool</div>
                    <h1 className={s.title}>{seasonName} Preseason</h1>
                    <p className={s.subtitle}>Get ready for another season of picks, standings, and bragging rights.</p>
                </div>
                {isInitialLoadComplete && hasCapacityLimit ? <Badge bg={spotsLeftVariant} text={['light','warning'].includes(spotsLeftVariant) ? 'dark' : 'light'} className={s.spotsLeft}>{spotsLeft ? `${pluralize('spot', spotsLeft, true)} available` : 'League full'}</Badge> : null}
            </header>
            {registrationCheckCompleted ?
            <Row className={s.statusRow}>
                {hasKickoff ?
                <Col lg={6} xs={12} className={s.statusCol}>
                    <PreseasonCountdown nflKickoff={nflKickoff}
                        prefix='The NFL season kicks off in'
                        smallPrefix='NFL kickoff in'
                        elapsed='Game on!' />
                </Col>
                : null}
                <Col lg={hasKickoff ? 6 : 12} xs={12} className={s.statusCol}>

                {hasTeamRegistered ? 
                <section className={s.registrationConfirmation} aria-label='Team registration confirmed'>
                    <div className={s.confirmationIcon}><FontAwesomeIcon name='check' /></div>
                    <div className={s.confirmationCopy}>
                        <div className={s.confirmationEyebrow}>Registration complete</div>
                        <h2>You’re in for the {seasonName} season</h2>
                        <p>Your team is registered. We’ll let you know when it’s time to enter your Week 1 picks.</p>
                        {registeredTeam.teamName ?
                        <div className={s.registeredTeamDetails}>
                            <span><FontAwesomeIcon name='shield' /> {registeredTeam.teamName}</span>
                            {registeredDivision.divisionName ?
                                <span>{registeredDivision.divisionName} Division</span>
                            : null}
                        </div>
                        : null}
                    </div>
                </section>
                : null}

                {isTeamRegistrationOpen && !hasTeamRegistered && spotsLeft ? 
                    <div className={s.registrationFormCard}>
                    <PreseasonTeamRegistration 
                        teamRegistration={teamRegistration} 
                        divisions={divisions} 
                        entryFee={entryFee}
                        loadPreseasonData={loadPreseasonData}
                        checkTeamNameAvailability={checkTeamNameAvailability} 
                        playerId={playerId}
                        paymentEmail={paymentEmail}
                        userName={userName}
                        teamRegistrationReset={teamRegistrationReset}
                        teamRegistrationDivisionSelection={teamRegistrationDivisionSelection}
                        teamRegistrationIsPaid={teamRegistrationIsPaid}
                        teamRegistrationRegisterTeam={teamRegistrationRegisterTeam}
                        seasonId={seasonId} 
                        seasonName={seasonName} />
                    </div>
                : null}

                {!isTeamRegistrationOpen && !hasTeamRegistered && spotsLeft > 0 ?
                <Alert variant='info' className={s.alert}>
                    <strong>Registration is not open yet.</strong><br />
                    Check back soon to reserve your team for the {seasonName} season.
                </Alert>
                : null}

                {!hasTeamRegistered && spotsLeft === 0 ?
                <Alert variant='danger' className={s.alert}>
                    The {seasonName} league is full. Check back in case another spot becomes available.
                </Alert>
                : null}
                </Col>
            </Row>
            : null}
            <section className={s.divisionsSection}>
                <div className={s.sectionHeading}>
                    <div>
                        <div className={s.eyebrow}>The field</div>
                        <h2>League divisions</h2>
                    </div>
                    {hasRegisteredTeams ? <span className={s.teamCount}>{pluralize('team', totalRegistrations, true)} registered</span> : null}
                </div>
                {!hasRegisteredTeams ? <p className={s.sectionDescription}>Choose from these divisions when team registration opens.</p> : null}
                <Row className={s.divisionsRow}>{arrStandings}</Row>
            </section>
            {multipleLengths ?
            <aside className={s.rosterNotice}>
                <div className={s.rosterNoticeIcon}><FontAwesomeIcon name='scale-balanced' /></div>
                <div>
                    <strong>Division rosters may be adjusted</strong>
                    <p>Teams may move between divisions as we balance the field before the season begins.</p>
                </div>
            </aside>
            : null}
        </main>
    )
}

export default Preseason
